/**
 * CompetitionFuturesPlate — one futures square on a club competition: the country's flag as the
 * ground, the kit centred on top (specs/champions-league AC-15.11).
 *
 * This reverses AC-15.9, which said a club competition draws no flag anywhere. That rule still
 * holds wherever one mark stands for one team — the pick card, the standings table, the
 * leaderboard chip — because there a flag would claim Barcelona *is* Spain. Inside a plate the
 * kit is the identity and the flag is only the ground behind it, which is how the World Cup's
 * own scorer square has always been built.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { View } from 'react-native';
import { Image } from 'expo-image';
import { Trophy } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { getFlagCdnUrl } from '../../../src/worldcup/helpers/flagCdn.helper';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { SoccerKit } from '../../ui/SoccerKit';
import type { TeamKitType } from '../../../constants/teamKitColors';
import {
  COMPETITION_FUTURES_PLATE,
  COMPETITION_FUTURES_TEST_IDS,
} from './CompetitionFutures.constants';
import {
  getCompetitionFuturesPlateKitSize,
  getCompetitionFuturesPlateStyles,
} from './CompetitionFuturesPlate.styles';

export interface CompetitionFuturesPlateProps {
  pickKind: 'winner' | 'scorer';
  /**
   * The club's country for the winner square, the player's nationality for the scorer square.
   * Null, empty or unresolvable falls back to the UEFA flag (AC-15.12).
   */
  countryCode: string | null | undefined;
  /** Absent means nothing is picked yet: the winner square draws a trophy and no flag. */
  kit: { primary: string; secondary: string; type?: TeamKitType; number?: number } | null;
  /** Screen width, so the plate tracks the same breakpoints the rest of the card does. */
  windowWidth: number;
  accessibilityLabel?: string;
}

export const CompetitionFuturesPlate: React.FC<CompetitionFuturesPlateProps> = ({
  pickKind,
  countryCode,
  kit,
  windowWidth,
  accessibilityLabel,
}) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const styles = useMemo(
    () => getCompetitionFuturesPlateStyles(theme, windowWidth, themeMode),
    [theme, windowWidth, themeMode],
  );
  const kitSize = useMemo(() => getCompetitionFuturesPlateKitSize(windowWidth), [windowWidth]);

  const resolvedUri = countryCode ? getFlagCdnUrl(countryCode) : null;
  const fallbackUri = getFlagCdnUrl(COMPETITION_FUTURES_PLATE.FALLBACK_COUNTRY_CODE);

  /**
   * A country can resolve to a code the flag CDN does not actually carry (Albania, Malta and
   * Kosovo are all real Champions League qualifying countries in that position). A missing code
   * and a failed load are the same thing to the viewer, so they take the same fallback.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => setLoadFailed(false), [resolvedUri]);

  const usingFallback = !resolvedUri || loadFailed;
  const flagUri = usingFallback ? fallbackUri : resolvedUri;


  return (
    <View style={styles.plate}>
      {kit != null && flagUri ? (
        <>
          {/*
            A country flag is a full-bleed rectangle and simply covers the plate. The UEFA
            fallback is a flag centred in a transparent square, so it is drawn oversized in a
            centring layer and the plate clips its margins away — see FALLBACK_FLAG_OVERSCAN.
          */}
          {usingFallback ? (
            <View style={styles.fallbackFlagLayer}>
              <Image
                source={{ uri: flagUri, cacheKey: flagUri }}
                style={styles.fallbackFlag}
                contentFit="contain"
                testID={COMPETITION_FUTURES_TEST_IDS.plateFlag(pickKind)}
                accessibilityLabel={accessibilityLabel}
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            </View>
          ) : (
            <Image
              source={{ uri: flagUri, cacheKey: flagUri }}
              style={styles.flag}
              contentFit="cover"
              onError={() => setLoadFailed(true)}
              testID={COMPETITION_FUTURES_TEST_IDS.plateFlag(pickKind)}
              accessibilityLabel={accessibilityLabel}
              alt={WEB_IMAGE_ALT_DECORATIVE}
            />
          )}
          <View style={styles.scrim} />
        </>
      ) : null}

      <View style={styles.kitLayer}>
        {kit != null ? (
          <View testID={COMPETITION_FUTURES_TEST_IDS.plateKit(pickKind)}>
            <SoccerKit
              size={kitSize}
              primary={kit.primary}
              secondary={kit.secondary}
              type={kit.type}
              number={kit.number}
            />
          </View>
        ) : (
          // Nothing picked: there is no club, so there is no country to stand behind one
          // (AC-15.13). A trophy plate, the way the card shipped.
          <View testID={COMPETITION_FUTURES_TEST_IDS.plateEmpty}>
            <Trophy size={Math.round(kitSize * 0.5)} color={theme.gray[600]} />
          </View>
        )}
      </View>
    </View>
  );
};
